import React, { useEffect, useMemo, useState } from "react";
import {
  getSuppliers,
  addSupplier,
  updateSupplier,
  deleteSupplier,
} from "../../services/apiService";
import "./AddSupplier.css";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaTimes,
  FaSearch,
  FaBuilding,
} from "react-icons/fa";

const DEFAULT_FORM = {
  SupplierName: "",
  ContactPerson: "",
  Address: "",
  Email: "",
  Phone: "",
  Source: "Local",
  Currency: "PHP",
};

export default function AddSupplier() {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierForm, setSupplierForm] = useState(DEFAULT_FORM);

  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [currencyFilter, setCurrencyFilter] = useState("All");

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const data = await getSuppliers();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch suppliers error:", err);
      alert("Failed to fetch suppliers.");
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = useMemo(() => {
    let results = [...suppliers];

    if (sourceFilter !== "All") {
      results = results.filter((s) => (s.Source || "Local") === sourceFilter);
    }

    if (currencyFilter !== "All") {
      results = results.filter((s) => (s.Currency || "PHP") === currencyFilter);
    }

    if (search.trim()) {
      const keyword = search.trim().toLowerCase();

      results = results.filter((s) => {
        return (
          (s.SupplierName || "").toLowerCase().includes(keyword) ||
          (s.ContactPerson || "").toLowerCase().includes(keyword) ||
          (s.Address || "").toLowerCase().includes(keyword) ||
          (s.Email || "").toLowerCase().includes(keyword) ||
          (s.Phone || "").toLowerCase().includes(keyword) ||
          (s.Source || "").toLowerCase().includes(keyword) ||
          (s.Currency || "").toLowerCase().includes(keyword)
        );
      });
    }

    results.sort((a, b) =>
      (a.SupplierName || "").localeCompare(b.SupplierName || "")
    );

    return results;
  }, [suppliers, search, sourceFilter, currencyFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sourceFilter, currencyFilter]);

  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage) || 1;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentSuppliers = filteredSuppliers.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const localCount = suppliers.filter(
    (s) => (s.Source || "Local") === "Local"
  ).length;

  const foreignCount = suppliers.filter((s) => s.Source === "Foreign").length;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSupplierForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openAddModal = () => {
    setSupplierForm(DEFAULT_FORM);
    setEditId(null);
    setShowModal(true);
  };

  const handleEdit = (supplier) => {
    setSupplierForm({
      SupplierName: supplier.SupplierName || "",
      ContactPerson: supplier.ContactPerson || "",
      Address: supplier.Address || "",
      Email: supplier.Email || "",
      Phone: supplier.Phone || "",
      Source: supplier.Source || "Local",
      Currency: supplier.Currency || "PHP",
    });

    setEditId(supplier.SupplierID);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    setEditId(null);
    setSupplierForm(DEFAULT_FORM);
  };

  const validateForm = () => {
    if (!supplierForm.SupplierName.trim()) {
      alert("Supplier name is required.");
      return false;
    }

    if (!supplierForm.Address.trim()) {
      alert("Address is required.");
      return false;
    }

    if (!supplierForm.Source) {
      alert("Source is required.");
      return false;
    }

    if (!supplierForm.Currency) {
      alert("Currency is required.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      SupplierName: supplierForm.SupplierName.trim(),
      ContactPerson: supplierForm.ContactPerson.trim(),
      Address: supplierForm.Address.trim(),
      Email: supplierForm.Email.trim(),
      Phone: supplierForm.Phone.trim(),
      Source: supplierForm.Source,
      Currency: supplierForm.Currency,
    };

    try {
      setSaving(true);

      if (editId) {
        await updateSupplier(editId, payload);
        alert("Supplier updated successfully!");
      } else {
        await addSupplier(payload);
        alert("Supplier added successfully!");
      }

      closeModal();
      await fetchSuppliers();
    } catch (err) {
      console.error("Save supplier error:", err);
      alert(
        err?.response?.data?.error ||
          err?.message ||
          "Something went wrong while saving supplier."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this supplier?"
    );

    if (!confirmDelete) return;

    try {
      await deleteSupplier(id);
      alert("Supplier deleted successfully!");
      await fetchSuppliers();
    } catch (err) {
      console.error("Delete supplier error:", err);
      alert(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to delete supplier."
      );
    }
  };

  return (
    <div className="supplier-container">
      <div className="supplier-page-header">
        <div className="supplier-title-wrap">
          <div className="supplier-icon-box">
            <FaBuilding />
          </div>

          <div>
            <h2>Supplier Management</h2>
            <p>
              Manage supplier records, contact details, source type, and
              currency.
            </p>
          </div>
        </div>

        <button className="add-supplier-btn" onClick={openAddModal}>
          <FaPlus />
          <span>Add Supplier</span>
        </button>
      </div>

      <div className="supplier-toolbar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search supplier, contact person, email, phone, address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="supplier-search"
          />
        </div>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="supplier-filter-select"
        >
          <option value="All">All Sources</option>
          <option value="Local">Local</option>
          <option value="Foreign">Foreign</option>
        </select>

        <select
          value={currencyFilter}
          onChange={(e) => setCurrencyFilter(e.target.value)}
          className="supplier-filter-select"
        >
          <option value="All">All Currencies</option>
          <option value="PHP">PHP</option>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="JPY">JPY</option>
          <option value="CNY">CNY</option>
        </select>
      </div>

      <div className="supplier-summary-cards">
        <div className="summary-card">
          <span>Total Suppliers</span>
          <strong>{suppliers.length}</strong>
        </div>

        <div className="summary-card">
          <span>Local</span>
          <strong>{localCount}</strong>
        </div>

        <div className="summary-card">
          <span>Foreign</span>
          <strong>{foreignCount}</strong>
        </div>
      </div>

      <div className="supplier-table-card">
        <table className="supplier-table">
          <thead>
            <tr>
              <th>Supplier Name</th>
              <th>Contact Person</th>
              <th>Email</th>
              <th>Contact Number</th>
              <th>Address</th>
              <th>Source</th>
              <th>Currency</th>
              <th className="actions-header">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="empty-state">
                  Loading suppliers...
                </td>
              </tr>
            ) : currentSuppliers.length > 0 ? (
              currentSuppliers.map((s) => (
                <tr key={s.SupplierID}>
                  <td className="supplier-name-cell">{s.SupplierName}</td>

                  <td>{s.ContactPerson || "—"}</td>

                  <td>{s.Email || "—"}</td>

                  <td>{s.Phone || "—"}</td>

                  <td>{s.Address || "—"}</td>

                  <td>
                    <span
                      className={`source-badge ${
                        s.Source === "Foreign"
                          ? "source-foreign"
                          : "source-local"
                      }`}
                    >
                      {s.Source || "Local"}
                    </span>
                  </td>

                  <td>
                    <span className="currency-badge">
                      {s.Currency || "PHP"}
                    </span>
                  </td>

                  <td className="actions-cell">
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(s)}
                        className="edit-btn"
                        title="Edit Supplier"
                        type="button"
                      >
                        <FaEdit />
                      </button>

                      <button
                        onClick={() => handleDelete(s.SupplierID)}
                        className="delete-btn"
                        title="Delete Supplier"
                        type="button"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="empty-state">
                  No suppliers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredSuppliers.length > 0 && (
        <div className="supplier-footer">
          <span>
            Showing {indexOfFirstItem + 1}-
            {Math.min(indexOfLastItem, filteredSuppliers.length)} of{" "}
            {filteredSuppliers.length}
          </span>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((prev) => Math.max(prev - 1, 1))
                }
                disabled={currentPage === 1}
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={currentPage === index + 1 ? "active" : ""}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="supplier-modal-overlay">
          <div className="supplier-modal">
            <div className="supplier-modal-header">
              <div>
                <h3>{editId ? "Edit Supplier" : "Add Supplier"}</h3>
                <p>
                  {editId
                    ? "Update supplier information below."
                    : "Fill in supplier information below."}
                </p>
              </div>

              <button
                type="button"
                className="modal-close-btn"
                onClick={closeModal}
                disabled={saving}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="supplier-form">
              <div className="form-group full-width">
                <label>
                  Supplier Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="SupplierName"
                  value={supplierForm.SupplierName}
                  onChange={handleChange}
                  placeholder="Enter supplier name"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Contact Person</label>
                <input
                  type="text"
                  name="ContactPerson"
                  value={supplierForm.ContactPerson}
                  onChange={handleChange}
                  placeholder="Enter contact person"
                />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="Email"
                    value={supplierForm.Email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                  />
                </div>

                <div className="form-group">
                  <label>Contact Number</label>
                  <input
                    type="text"
                    name="Phone"
                    value={supplierForm.Phone}
                    onChange={handleChange}
                    placeholder="Enter contact number"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="Address"
                  value={supplierForm.Address}
                  onChange={handleChange}
                  placeholder="Enter supplier address"
                  required
                />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label>
                    Source <span className="required">*</span>
                  </label>
                  <select
                    name="Source"
                    value={supplierForm.Source}
                    onChange={handleChange}
                    required
                  >
                    <option value="Local">Local</option>
                    <option value="Foreign">Foreign</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Currency <span className="required">*</span>
                  </label>
                  <select
                    name="Currency"
                    value={supplierForm.Currency}
                    onChange={handleChange}
                    required
                  >
                    <option value="PHP">PHP</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="JPY">JPY</option>
                    <option value="CNY">CNY</option>
                  </select>
                </div>
              </div>

              <div className="form-buttons">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button type="submit" className="save-btn" disabled={saving}>
                  {saving
                    ? "Saving..."
                    : editId
                    ? "Update Supplier"
                    : "Save Supplier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}